import { createReducer, on } from '@ngrx/store';
import { closingAdjustmentActions } from '../actions/closing-adjustment.actions';
import {
    IAdjustmentCandidate,
    IAdjustmentCounts,
    IAdjustmentListItem,
    IAdjustmentResultSummary,
    IAdjustmentTransferOptions,
    IAdjustmentTransferResponse,
} from '../models/closing-adjustment.model';
import { AdjustmentFilterKey, AdjustmentSort } from '../types/closing-adjustments.types';

/**
 * REV-21375 — the correction-booking slice's state (ADR-0015).
 *
 * ⚠️ Nothing here is DERIVED from something else in it. In particular the four counts, the three
 * result figures and `readOnly` come from the server and are never recomputed from `adjustments`: the
 * counts span the whole closing-year while `adjustments` may be one filtered pill, the result figures
 * are a pure function of the CLOSING's accounts rather than of the rows on screen (§11), and which year
 * is current is the closing's balance-sheet date rather than the clock's.
 */
export interface ClosingAdjustmentState {
    /**
     * Which closing this slice is about. Real state, not effect bookkeeping — the reload effects need
     * it, and holding it here keeps the effects stateless. An effect instance field would survive
     * across route changes and quietly reload the wrong project.
     */
    projectId: string | null;

    adjustments: IAdjustmentListItem[];
    /**
     * The ids the transfer control sends, straight from the server.
     *
     * NOT derived from `adjustments`: that array is the filtered page, so while the user was on
     * `Gebucht` it held no open rows and a derived transfer set was empty — the control then did
     * nothing, silently, next to a count that said otherwise.
     */
    transferableAdjustmentIds: string[];
    /** From the server. ⚠️ `all` is NOT `open + booked` — `transferred` is a state of its own (§2). */
    counts: IAdjustmentCounts;
    /** The three derived figures plus the list total. `null` until the first load. */
    result: IAdjustmentResultSummary | null;
    transferOptions: IAdjustmentTransferOptions;

    /** The current year and the two before it, newest first (§10). */
    availableFiscalYears: number[];
    /**
     * The closing's balance-sheet date, `YYYY-MM-DD`. Empty until the first list response.
     *
     * Held because the entry dialog pre-fills its booking date from it. The view is GIVEN this rather
     * than deriving it: the fiscal year lives on the closing, and a client that computed the default
     * would be a second copy of a rule only the backend enforces.
     */
    balanceSheetDate: string;
    selectedFiscalYear: number | null;
    /** A PRIOR year — the whole surface is read-only. The SERVER's answer, not a derivation. */
    readOnly: boolean;

    /**
     * The IN-ACCOUNT list (REV-21364), kept apart from everything above.
     *
     * A separate branch rather than reusing `adjustments`: the two lists answer different questions
     * — "the closing's corrections" and "this account's" — and are reached by different routes. One
     * array would mean the account pane's load overwrote the register's rows, and the register's year
     * change moved the pane's year.
     */
    account: {
        accountId: string | null;
        /**
         * The closing this pane's list belongs to — carried HERE and not read from the top-level
         * `projectId`.
         *
         * ⚠️ That field is stamped by the REGISTER's load, so on a deep link straight into an account
         * (or a page reload while standing on one) it is still `null` and nothing can reload this
         * branch. The two lists are kept apart precisely so neither depends on the other having run,
         * and a reload key that only exists once the other surface has been visited would break that.
         */
        projectId: string | null;
        adjustments: IAdjustmentListItem[];
        availableFiscalYears: number[];
        selectedFiscalYear: number | null;
        readOnly: boolean;
        /** `n offen` — the surface drops the badge at 0 rather than rendering "0 offen". */
        openCount: number;
        loading: boolean;
        error: boolean;
    };

    // ── Display state the host owns (AC12) ───────────────────────────────────────────────────────
    activeFilter: AdjustmentFilterKey;
    sort: AdjustmentSort | null;
    searchTerm: string;

    loading: boolean;
    error: boolean;

    // ── The detail route ─────────────────────────────────────────────────────────────────────────
    adjustment: IAdjustmentListItem | null;
    /** The lines the reconciliation FOUND — non-empty exactly when the correction is ambiguous (§4). */
    candidates: IAdjustmentCandidate[];
    adjustmentLoading: boolean;
    adjustmentError: boolean;

    // ── Writes ───────────────────────────────────────────────────────────────────────────────────
    /** A create, edit, discard or transfer is in flight. The surfaces stop accepting clicks. */
    writePending: boolean;
    /** A refusal, as a translation KEY — never server prose (AC13). */
    writeErrorKey: string | null;
    /**
     * The last transfer's outcome, so the surface can report count, total and the per-position result
     * (AC5).
     *
     * `null` until the user transfers once in this visit.
     */
    lastTransfer: IAdjustmentTransferResponse | null;
}

export const initialClosingAdjustmentState: ClosingAdjustmentState = {
    projectId: null,
    // Empty until the first list response answers it. The entry dialog then leaves its date field
    // blank, and the server applies the same default — so the two never disagree.
    balanceSheetDate: '',
    adjustments: [],
    transferableAdjustmentIds: [],
    counts: { all: 0, released: 0, transferred: 0, booked: 0, discarded: 0 },
    account: {
        accountId: null,
        projectId: null,
        adjustments: [],
        availableFiscalYears: [],
        selectedFiscalYear: null,
        readOnly: false,
        openCount: 0,
        loading: false,
        error: false,
    },
    result: null,
    // ⚠️ The default says NOTHING is connected. A default of "can write" would offer
    // `Direkt ans ERP senden` for a mandate whose system cannot take a posting, and the failure would
    // only show after the user pressed it (§3a rule 3).
    transferOptions: { canWriteToErp: false, reasonKey: null, connected: null },
    availableFiscalYears: [],
    selectedFiscalYear: null,
    // `false` rather than `true`: a surface that started read-only would hide the controls on the first
    // paint of the CURRENT year and then reveal them, which reads as a permissions glitch.
    readOnly: false,
    activeFilter: 'all',
    sort: null,
    searchTerm: '',
    loading: false,
    error: false,
    adjustment: null,
    candidates: [],
    adjustmentLoading: false,
    adjustmentError: false,
    writePending: false,
    writeErrorKey: null,
    lastTransfer: null,
};

export const closingAdjustmentReducer = createReducer(
    initialClosingAdjustmentState,

    // ── The closing-wide read ────────────────────────────────────────────────────────────────────
    on(closingAdjustmentActions.loadAdjustments, (state, { projectId, fiscalYear, filter }) => ({
        ...state,
        projectId,
        // Held so a reload can re-ask for the same thing. `fiscalYear: null` keeps whatever the server
        // last reported as current rather than blanking it — otherwise the year selector would empty
        // itself on every reload.
        //
        // ⚠️ ABER NUR INNERHALB DESSELBEN ABSCHLUSSES. Die angebotenen Jahre sind das Bilanzstichtags-
        // jahr des Abschlusses und die zwei davor — ein ANDERER Abschluss bietet andere Jahre an.
        // Wer das gehaltene Jahr über den Abschlusswechsel mitnimmt, schickt dem neuen Abschluss ein
        // Jahr, das dieser nicht kennt, und der Server weist es mit 400 `PRIOR_YEAR` ab — die Liste
        // bleibt leer, obwohl Buchungen da sind. Gesehen mit einem 2025er-Abschluss vor einem
        // 2024er: `?fiscalYear=2025` gegen ein Angebot von [2024, 2023, 2022]. Beim Wechsel wird
        // deshalb auf `null` zurückgesetzt und der Server bestimmt das Jahr wieder selbst.
        selectedFiscalYear:
            projectId === state.projectId ? (fiscalYear ?? state.selectedFiscalYear) : (fiscalYear ?? null),
        activeFilter: filter,
        loading: true,
        error: false,
        // Every load of this list is the user's step — opening the tab, a pill, a year, a retry — so the
        // previous rows go and the register shows its placeholders until the new ones land. The badge
        // reads `counts`, which stays.
        adjustments: [],
    })),
    on(closingAdjustmentActions.loadAdjustmentsSuccess, (state, { response }) => ({
        ...state,
        adjustments: response.adjustments,
        transferableAdjustmentIds: response.transferableAdjustmentIds,
        counts: response.counts,
        result: response.result,
        transferOptions: response.transferOptions,
        availableFiscalYears: response.availableFiscalYears,
        balanceSheetDate: response.balanceSheetDate,
        // From the RESPONSE, so the surface and the server agree on which year is being shown even when
        // the request omitted it.
        selectedFiscalYear: response.fiscalYear,
        readOnly: response.readOnly,
        loading: false,
        error: false,
    })),
    on(closingAdjustmentActions.loadAdjustmentsFailure, (state) => ({
        ...state,
        // The rows are DROPPED on failure. Keeping them would leave the previous year's list under the
        // error notice, which reads as "these are the corrections and something also went wrong".
        adjustments: [],
        // And with them the transfer set: offering to transfer ids from a list that failed to load
        // would act on a year the user is no longer being shown.
        transferableAdjustmentIds: [],
        loading: false,
        error: true,
    })),

    // ── Display state ────────────────────────────────────────────────────────────────────────────
    on(closingAdjustmentActions.setFilter, (state, { filter }) => ({ ...state, activeFilter: filter })),
    on(closingAdjustmentActions.setFiscalYear, (state, { fiscalYear }) => ({
        ...state,
        selectedFiscalYear: fiscalYear,
    })),
    on(closingAdjustmentActions.setSort, (state, { sort }) => ({ ...state, sort })),
    on(closingAdjustmentActions.setSearchTerm, (state, { searchTerm }) => ({ ...state, searchTerm })),

    // ── The detail route ─────────────────────────────────────────────────────────────────────────
    on(closingAdjustmentActions.loadAdjustment, (state) => ({
        ...state,
        adjustmentLoading: true,
        adjustmentError: false,
    })),
    on(closingAdjustmentActions.loadAdjustmentSuccess, (state, { response }) => ({
        ...state,
        adjustment: response.adjustment,
        candidates: response.candidates,
        transferOptions: response.transferOptions,
        adjustmentLoading: false,
        adjustmentError: false,
    })),
    on(closingAdjustmentActions.loadAdjustmentFailure, (state) => ({
        ...state,
        adjustment: null,
        candidates: [],
        adjustmentLoading: false,
        adjustmentError: true,
    })),
    on(closingAdjustmentActions.clearAdjustment, (state) => ({
        ...state,
        adjustment: null,
        candidates: [],
        adjustmentError: false,
    })),

    // ── Writes ───────────────────────────────────────────────────────────────────────────────────
    on(
        closingAdjustmentActions.createAdjustment,
        closingAdjustmentActions.editAdjustment,
        closingAdjustmentActions.discardAdjustment,
        closingAdjustmentActions.restoreAdjustment,
        closingAdjustmentActions.transferAdjustments,
        closingAdjustmentActions.resolveAmbiguity,
        (state) => ({ ...state, writePending: true, writeErrorKey: null }),
    ),
    on(
        closingAdjustmentActions.createAdjustmentSuccess,
        closingAdjustmentActions.editAdjustmentSuccess,
        closingAdjustmentActions.resolveAmbiguitySuccess,
        (state, { response }) => ({
            ...state,
            // The detail slot follows the write, so the surface the user is on shows the result without
            // a second request.
            adjustment: response.adjustment,
            candidates: response.candidates,
            writePending: false,
            writeErrorKey: null,
        }),
    ),
    on(closingAdjustmentActions.restoreAdjustmentSuccess, (state) => ({
        ...state,
        // The restored correction rejoins the working lists. The detail slot is cleared and the reload
        // effect fetches it again — the row now differs in more than one field (`discardedAt`, and the
        // audit trail gained an entry), and re-deriving it here would be a second opinion about what
        // the server just stored.
        adjustment: null,
        candidates: [],
        writePending: false,
        writeErrorKey: null,
    })),
    on(closingAdjustmentActions.discardAdjustmentSuccess, (state) => ({
        ...state,
        // The discarded correction leaves every working list, so the detail slot is cleared rather than
        // left holding a row the reload is about to drop.
        adjustment: null,
        candidates: [],
        writePending: false,
        writeErrorKey: null,
    })),
    on(closingAdjustmentActions.transferAdjustmentsSuccess, (state, { response }) => ({
        ...state,
        lastTransfer: response,
        writePending: false,
        writeErrorKey: null,
    })),
    on(closingAdjustmentActions.clearTransferResult, (state) => ({ ...state, lastTransfer: null })),
    on(
        closingAdjustmentActions.createAdjustmentFailure,
        closingAdjustmentActions.editAdjustmentFailure,
        closingAdjustmentActions.discardAdjustmentFailure,
        closingAdjustmentActions.transferAdjustmentsFailure,
        closingAdjustmentActions.resolveAmbiguityFailure,
        closingAdjustmentActions.restoreAdjustmentFailure,
        (state, { errorKey }) => ({ ...state, writePending: false, writeErrorKey: errorKey }),
    ),
    // ── The IN-ACCOUNT list (REV-21364) ─────────────────────────────────────────────────────────
    on(closingAdjustmentActions.loadAccountAdjustments, (state, { accountId, projectId, background }) => ({
        ...state,
        account: {
            ...state.account,
            // Carried so a late response for a PREVIOUS account cannot land on this one.
            accountId,
            // Carried so a WRITE can reload this branch on its own — see the field's note.
            projectId,
            loading: true,
            error: false,
            // Opening the account or a year tab is the user's step: the old rows go and the pane shows its
            // placeholders. The reload after a write keeps them — the pane does not flash on every save.
            adjustments: background === true ? state.account.adjustments : [],
        },
    })),
    on(closingAdjustmentActions.loadAccountAdjustmentsSuccess, (state, { response }) => ({
        ...state,
        account: {
            ...state.account,
            adjustments: response.adjustments,
            availableFiscalYears: response.availableFiscalYears,
            // From the RESPONSE, so the pane and the server agree on the year even when the request
            // omitted it — the same reason the register reads it back.
            selectedFiscalYear: response.fiscalYear,
            readOnly: response.readOnly,
            openCount: response.openCount,
            loading: false,
            error: false,
        },
    })),
    on(closingAdjustmentActions.loadAccountAdjustmentsFailure, (state) => ({
        ...state,
        account: {
            ...state.account,
            // Rows dropped, like the register does: keeping them under an error notice reads as
            // "these are the corrections and something also went wrong".
            adjustments: [],
            openCount: 0,
            loading: false,
            error: true,
        },
    })),
    on(closingAdjustmentActions.setAccountFiscalYear, (state, { fiscalYear }) => ({
        ...state,
        account: { ...state.account, selectedFiscalYear: fiscalYear },
    })),
    on(closingAdjustmentActions.clearAccountAdjustments, (state) => ({
        ...state,
        account: initialClosingAdjustmentState.account,
    })),

);
